import { useCallback } from 'react';

import { SafeStorage } from '@/domains/core/data/utils/SafeStorage/SafeStorage';
import { getOrCreateVisitorId } from '@/domains/core/tracking/utils/visitorId';
import { useAuth } from '@/domains/customerManagement/auth/hooks/useAuth';

export const PLAYLIST_RECONCILIATION_LOCAL_STORAGE_KEY =
  'mm_playlist_reconciliation';

/**
 * This hook retrieves Playlist reconciliation and playlist fingerprint from local
 * storage and provides helper methods to generate, persist and clear this data
 */
export const usePlaylistReconciliation = () => {
  const { userId } = useAuth();
  const safeLocalStorage = SafeStorage.getLocalStorage();

  const getPlaylistReconciliationFromLocalStorage = useCallback(() => {
    const savedData = safeLocalStorage.getItem(
      PLAYLIST_RECONCILIATION_LOCAL_STORAGE_KEY,
    );
    if (savedData) {
      return savedData;
    }
    return {};
  }, [safeLocalStorage]);

  const { playlistFingerprint = '', playlistReconciliation = '' } =
    getPlaylistReconciliationFromLocalStorage();

  const createPlaylistReconciliation = useCallback(
    async (engineName: string, eventTrigger: string) => {
      const visitorId = await getOrCreateVisitorId();
      const timestamp = Date.now();
      const user = userId || visitorId;
      return {
        playlistFingerprint: `${engineName}-${user}-${timestamp}`,
        playlistReconciliation: `${engineName}-${eventTrigger}`,
      };
    },
    [userId],
  );

  const persistPlaylistReconciliation = useCallback(
    (fingerprint: string, reconciliation: string, productId: string) => {
      safeLocalStorage.setItem(PLAYLIST_RECONCILIATION_LOCAL_STORAGE_KEY, {
        playlistFingerprint: fingerprint,
        playlistReconciliation: reconciliation,
        productId,
      });
    },
    [safeLocalStorage],
  );

  const clearPlaylistReconciliation = () => {
    safeLocalStorage.removeItem(PLAYLIST_RECONCILIATION_LOCAL_STORAGE_KEY);
  };

  return {
    playlistFingerprint,
    playlistReconciliation,
    getPlaylistReconciliationFromLocalStorage,
    createPlaylistReconciliation,
    persistPlaylistReconciliation,
    clearPlaylistReconciliation,
  };
};
